import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { mensajeAmigable } from '../utils/errores';
import { obtenerCotizaciones, obtenerItemsCotizados } from '../services/cotizacionesService';
import { ESTADOS_COTIZACION, estadoInfo } from '../utils/cotizaciones';
import { formatoMoneda } from '../utils/servicios';

const P = 'var(--color-primario,#DB2777)';

// Reporte del módulo de Servicios/Cotizaciones. Solo lectura, a partir de lo
// que ya se guarda (cotizaciones + sus renglones). Sin migraciones nuevas.
export default function ReportesCotizacionesView() {
  const { negocio } = useAuth();
  const moneda = negocio?.moneda || 'L';
  const fmt = (v) => formatoMoneda(v, moneda);
  const [cotizaciones, setCotizaciones] = useState(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([obtenerCotizaciones(), obtenerItemsCotizados()])
      .then(([cs, its]) => { setCotizaciones(cs || []); setItems(its || []); })
      .catch((e) => setError(mensajeAmigable(e)));
  }, []);

  const m = useMemo(() => {
    const cots = cotizaciones || [];
    const porEstado = {};
    ESTADOS_COTIZACION.forEach((e) => { porEstado[e.clave] = 0; });
    let ganado = 0, porConvertir = 0, enRevision = 0;
    cots.forEach((c) => {
      porEstado[c.estado] = (porEstado[c.estado] || 0) + 1;
      const t = Number(c.total) || 0;
      if (c.estado === 'convertida') ganado += t;
      else if (c.estado === 'aprobada') porConvertir += t;
      else if (c.estado === 'enviada' || c.estado === 'vista') enRevision += t;
    });
    const total = cots.length;
    // "Enviadas" = las que salieron al cliente (excluye borradores y canceladas).
    const enviadas = total - (porEstado.borrador || 0) - (porEstado.cancelada || 0);
    const convertidas = porEstado.convertida || 0;
    const tasa = enviadas > 0 ? Math.round((convertidas / enviadas) * 100) : 0;

    // Lo más cotizado (agrupa renglones por descripción).
    const mapa = {};
    items.forEach((it) => {
      const k = (it.descripcion || 'Sin nombre').trim();
      if (!mapa[k]) mapa[k] = { nombre: k, veces: 0, monto: 0 };
      mapa[k].veces += 1;
      mapa[k].monto += Number(it.total) || 0;
    });
    const top = Object.values(mapa).sort((a, b) => b.veces - a.veces).slice(0, 8);

    return { porEstado, total, enviadas, convertidas, aprobadas: porEstado.aprobada || 0, tasa, ganado, porConvertir, enRevision, top };
  }, [cotizaciones, items]);

  if (error) return <p style={{ textAlign: 'center', padding: '24px', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>;
  if (!cotizaciones) return <div style={{ textAlign: 'center', padding: '32px', color: '#6B7280' }}>⏳ Cargando reportes…</div>;
  if (m.total === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 16px', color: '#6B7280' }}>
        <div style={{ fontSize: '44px' }}>📊</div>
        <p style={{ fontWeight: 700, color: '#111827', margin: '8px 0 4px' }}>Aún no hay datos para el reporte</p>
        <p style={{ fontSize: '14px', margin: 0 }}>Crea y envía cotizaciones para empezar a ver tus números aquí.</p>
      </div>
    );
  }

  const maxEstado = Math.max(1, ...ESTADOS_COTIZACION.map((e) => m.porEstado[e.clave]));
  const maxTop = Math.max(1, ...m.top.map((t) => t.veces));

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      {/* Tarjetas resumen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
        <Tarjeta titulo="Cotizaciones" valor={m.total} sub="en total" />
        <Tarjeta titulo="Tasa de conversión" valor={`${m.tasa}%`} sub={`${m.convertidas} de ${m.enviadas} enviadas`} destacado />
        <Tarjeta titulo="Ganado (convertidas)" valor={fmt(m.ganado)} sub="ya son ventas" color="#047857" />
        <Tarjeta titulo="Aprobadas por convertir" valor={fmt(m.porConvertir)} sub={`${m.aprobadas} cotización(es)`} color="#B45309" />
        <Tarjeta titulo="En revisión del cliente" valor={fmt(m.enRevision)} sub="enviadas / vistas" color="#6B7280" />
      </div>

      {/* Embudo por estado */}
      <div className="k-card" style={{ padding: '16px' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: '#111827' }}>📶 Cotizaciones por estado</h3>
        <div style={{ display: 'grid', gap: '8px' }}>
          {ESTADOS_COTIZACION.filter((e) => m.porEstado[e.clave] > 0).map((e) => {
            const n = m.porEstado[e.clave];
            const info = estadoInfo(e.clave);
            return (
              <div key={e.clave} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ width: '135px', fontSize: '12.5px', color: '#374151', flexShrink: 0 }}>{e.nombre}</span>
                <div style={{ flex: 1, background: '#F3F4F6', borderRadius: '999px', height: '14px', overflow: 'hidden' }}>
                  <div style={{ width: `${(n / maxEstado) * 100}%`, height: '100%', background: info.color, borderRadius: '999px', minWidth: '6px' }} />
                </div>
                <span style={{ width: '30px', textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#111827' }}>{n}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lo más cotizado */}
      <div className="k-card" style={{ padding: '16px' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: '#111827' }}>🏆 Lo más cotizado</h3>
        {m.top.length === 0 ? (
          <p style={{ fontSize: '13.5px', color: '#9CA3AF', margin: 0 }}>Todavía no hay renglones cotizados.</p>
        ) : (
          <div style={{ display: 'grid', gap: '10px' }}>
            {m.top.map((t, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '3px' }}>
                  <span style={{ fontSize: '13.5px', color: '#111827', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.nombre}</span>
                  <span style={{ fontSize: '12.5px', color: '#6B7280', flexShrink: 0 }}>{t.veces}× · {fmt(t.monto)}</span>
                </div>
                <div style={{ background: '#F3F4F6', borderRadius: '999px', height: '10px', overflow: 'hidden' }}>
                  <div style={{ width: `${(t.veces / maxTop) * 100}%`, height: '100%', background: P, borderRadius: '999px', minWidth: '6px' }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Tarjeta({ titulo, valor, sub, color, destacado }) {
  return (
    <div className="k-card" style={{ padding: '14px', borderTop: destacado ? `3px solid ${P}` : undefined }}>
      <p style={{ margin: 0, fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>{titulo}</p>
      <p style={{ margin: '4px 0 2px', fontSize: '22px', fontWeight: 800, color: color || (destacado ? P : '#111827') }}>{valor}</p>
      <p style={{ margin: 0, fontSize: '11.5px', color: '#9CA3AF' }}>{sub}</p>
    </div>
  );
}
